import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

// Brand assets
const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdfIcon = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function MiningTable11() {
  const pdfRef = useRef();

  const data = [
    {
      industry_label: "Support Activities for Mining",
      emp_2023: 1133,
      emp_2024: 1203,
      difference: 70,
      pct_change: 6.18,
    },
    {
      industry_label: "Metal Ore Mining",
      emp_2023: 634,
      emp_2024: 677,
      difference: 43,
      pct_change: 6.78,
    },
    {
      industry_label: "Oil and Gas Extraction",
      emp_2023: 354,
      emp_2024: 356,
      difference: 2,
      pct_change: 0.56,
    },
    {
      industry_label: "Coal Mining",
      emp_2023: 0,
      emp_2024: 0,
      difference: 0,
      pct_change: 0.0,
    },
    {
      industry_label: "Nonmetallic Mineral Mining and Quarrying",
      emp_2023: 487,
      emp_2024: 407,
      difference: -80,
      pct_change: -16.43,
    },
  ];

  // Chart Data
  const chartData = {
    labels: data.map((d) => d.industry_label),
    datasets: [
      {
        label: "2023 Employment",
        data: data.map((d) => d.emp_2023),
        backgroundColor: "#D8F999",
        borderRadius: 6,
      },
      {
        label: "2024 Employment",
        data: data.map((d) => d.emp_2024),
        backgroundColor: "#2C92B8",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "⛏️ Utah Mining Employment Change by Industry (2023–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Number of Employees" },
        beginAtZero: true,
      },
    },
  };

  // Insights
  const total2023 = data.reduce((sum, d) => sum + d.emp_2023, 0);
  const total2024 = data.reduce((sum, d) => sum + d.emp_2024, 0);
  const totalChange = total2024 - total2023;
  const overallPct = ((totalChange / total2023) * 100).toFixed(2);
  const topIndustry = data.reduce((max, d) =>
    d.pct_change > max.pct_change ? d : max,
  );
  const decliningIndustry = data.reduce((min, d) =>
    d.pct_change < min.pct_change ? d : min,
  );

  // PDF Export
   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text("20250916.v3.198", 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("utah_mining_employment_change.pdf");
       // input.style.width = originalWidth;
     });
   };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-blue-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Utah Mining Employment Change by Industry (2023–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment Growth
            </p>
            <p className="text-xl font-bold text-blue-800">
              +{totalChange.toLocaleString("en-US")} ({overallPct}%)
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Growing Sector
            </p>
            <p className="text-lg font-bold text-green-800">
              {topIndustry.industry_label} (+{topIndustry.pct_change}%)
            </p>
          </div>
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Declining Sector
            </p>
            <p className="text-lg font-bold text-red-800">
              {decliningIndustry.industry_label} ({decliningIndustry.pct_change}
              %)
            </p>
          </div>
        </div>

        {/* Chart + Table */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Industry</th>
                  <th className="p-2">2023</th>
                  <th className="p-2">2024</th>
                  <th className="p-2">Difference</th>
                  <th className="p-2">% Change</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.pct_change > 0
                        ? "bg-green-50 hover:bg-green-100"
                        : row.pct_change < 0
                          ? "bg-red-50 hover:bg-red-100"
                          : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 font-medium">{row.industry_label}</td>
                    <td className="p-2">{row.emp_2023.toLocaleString()}</td>
                    <td className="p-2">{row.emp_2024.toLocaleString()}</td>
                    <td
                      className={`p-2 ${
                        row.difference > 0
                          ? "text-green-700 font-semibold"
                          : row.difference < 0
                            ? "text-red-700 font-semibold"
                            : ""
                      }`}
                    >
                      {row.difference.toLocaleString("en-US")}
                    </td>
                    <td
                      className={`p-2 ${
                        row.pct_change > 0
                          ? "text-green-700 font-semibold"
                          : row.pct_change < 0
                            ? "text-red-700 font-semibold"
                            : ""
                      }`}
                    >
                      {row.pct_change}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-700 mt-4">
          ⛏️ Utah’s mining sector saw an overall <b>{overallPct}%</b> employment
          growth in 2024, led by <b>{topIndustry.industry_label}</b>, while
          <b> {decliningIndustry.industry_label}</b> recorded a decline.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
